import { Chip, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import { getTodayIsoFormat } from "../../util/schedule/scheduleEditorUtils";

const useStyles = makeStyles((theme) => ({
  shiftTimeStyle: {
    fontSize: "16px",
    fontWeight: "bold",
  },
  shiftPositionStyle: {
    fontSize: "12px",
  },
  shiftCardStyle: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaLightestBlue,
    borderLeft: "5px solid",
    borderColor: theme.palette.primary.swaBlue,
    textAlign: "left",
    marginBottom: "5px",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
  shiftTORCardStyle: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaLightYellow,
    borderLeft: "5px solid",
    borderColor: theme.palette.primary.swaYellow,
    textAlign: "left",
    marginBottom: "5px",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
  shiftTORCardStyleOnTemp: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaLightYellow + "80",
    borderLeft: "5px solid",
    borderColor: theme.palette.primary.swaYellow + "80",
    textAlign: "left",
    marginBottom: "5px",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
  shiftCardStyleOnTemp: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaLightestBlue + "80",
    borderLeft: "5px solid",
    borderColor: theme.palette.primary.swaBlue + "80",
    textAlign: "left",
    marginBottom: "5px",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
  shiftOffCardStyle: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaGray3,
    borderLeft: "5px solid",
    borderColor: "gray",
    textAlign: "left",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
  shiftOffCardStyleOnTemp: {
    marginLeft: "10px",
    marginRight: "10px",
    backgroundColor: theme.palette.secondary.swaGray1,
    borderLeft: "5px solid",
    borderColor: "lightgray",
    textAlign: "left",
    "& .MuiCardContent-root": {
      marginTop: "-12px",
      marginBottom: "-12px",
    },
  },
}));

const EmployeeDailySchedule = (props) => {
  const classes = useStyles();
  const currentDate = props.dayState.date ? props.dayState.date : props.dayState.additionalDate;
  const isEmployeeTemping = isEmployeeTempingOut(props.dayState.scheduleData, props.station);

  return (
    <>
      {
        <div data-testid={props.dayState.additionalDate ? "outsideMonthDate" : "currentMonthDate"}>
          {
            <Grid container>
              {isEmployeeTemping ? (
                <Chip
                  label="Temp"
                  sx={{
                    marginLeft: "5px",
                    marginTop: "5px",
                    marginBottom: "5px",
                    fontWeight: "normal",
                  }}
                />
              ) : (
                ""
              )}
              {currentDate === getTodayIsoFormat(props.station) ? (
                <Avatar
                  sx={{
                    marginLeft: "auto",
                    marginRight: "5px",
                    marginTop: "5px",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    backgroundColor: "primary.swaBlue",
                    textAlign: "center",
                    fontSize: "16px",
                  }}
                >
                  {currentDate.slice(-2).replace(/^0+/, "")}
                </Avatar>
              ) : (
                <Grid
                  sx={{
                    marginLeft: "auto",
                    marginBottom: "12px",
                    paddingRight: "15px",
                    paddingTop: "15px",
                    fontWeight: "normal",
                    fontSize: "16px",
                    color: "primary.swaBlue",
                  }}
                >
                  {currentDate.slice(-2).replace(/^0+/, "")}
                </Grid>
              )}
            </Grid>
          }
          {renderScheduleContent(
            props.dayState.scheduleData?.find((s) => s.station === props.station),
            classes,
            currentDate,
            isEmployeeTemping
          )}
        </div>
      }
    </>
  );
};

const renderScheduleContent = (schedule, classes, currentDate, isTemp) => {
  if (schedule?.isScheduledOff) {
    if (schedule?.shifts[0]?.createdBy === "TOR") {
      return (
        <Card className={getCardStyle(classes, schedule?.shifts[0], isTemp)}>
          <CardActionArea disabled={isTemp}>
            <CardContent>
              <Typography
                component="span"
                className={classes.shiftTimeStyle}
                sx={{ fontSize: "16px", color: isTemp ? "gray" : "black" }}
              >
                {schedule?.shifts[0]?.payCode}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    } else {
      return (
        <Card className={isTemp ? classes.shiftOffCardStyleOnTemp : classes.shiftOffCardStyle}>
          <CardActionArea disabled={isTemp}>
            <CardContent>
              <Typography component="span" sx={{ fontSize: "16px", color: isTemp ? "gray" : "black" }}>
                OFF
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    }
  } else if (schedule?.isNS) {
    return "";
  } else {
    return schedule?.shifts?.map((shift, index) => (
      <Card className={getCardStyle(classes, shift, isTemp)} key={currentDate + "_" + index}>
        <CardActionArea disabled={isTemp}>
          <CardContent>
            <Typography component="span" className={classes.shiftTimeStyle} sx={{ color: isTemp ? "gray" : "black" }}>
              {shift.isOff ? shift.payCode : `${shift.start} - ${shift.end}`}
            </Typography>
            {shift.isOff ? (
              ""
            ) : (
              <Typography className={classes.shiftPositionStyle} sx={{ color: isTemp ? "gray" : "black" }}>
                {shift.position} / {shift.assignment}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    ));
  }
};

const getCardStyle = (classes, shift, isTemp) => {
  if (isTemp) {
    if (shift.isOff) {
      return classes.shiftTORCardStyleOnTemp;
    } else {
      return classes.shiftCardStyleOnTemp;
    }
  } else {
    if (shift.isOff) {
      return classes.shiftTORCardStyle;
    } else {
      return classes.shiftCardStyle;
    }
  }
};

const isEmployeeTempingOut = (schedules, station) => {
  // Will return true if employee has a (outgoing) temp out of current/selected station
  return schedules?.some((schedule) => schedule.isTemp && schedule.station !== station);
};

EmployeeDailySchedule.propTypes = {
  dayState: PropTypes.object.isRequired,
  station: PropTypes.string.isRequired,
};

export default EmployeeDailySchedule;
